/**
 * Baked backgrounds: one pre-cropped image per banner, saved by the default
 * scene, in place of one shared image plus a crop transform.
 *
 * A default scene row for background N can carry:
 *
 *   - `bakedBgs[templateId]`    — the URL of the image cut to the banner's
 *                                 background box (2x pixels). The banner shows
 *                                 it at the full box size, with no transform.
 *   - `bakedBgMeta[templateId]` — what the image was cut from: the source URL,
 *                                 the crop transform, and the box size.
 *
 * The row also keeps the source crop (`bgX` … `bgFlip`, and `customBgs` for a
 * preset image). Code that does not know baked backgrounds reads those, and
 * until 2026-10-30 staging and production shared one database. The meta is
 * the guard for that period: when a more specific layer, or a writer that does
 * not know baked backgrounds, changes the crop or the preset image, the baked
 * image no longer agrees with its meta and is dropped, so the banner uses the
 * crop again.
 *
 * A baked banner keeps its positioning controls, unless its image is a ready
 * cut (layout/BgTransformer/templateBg.ts). It shows the cut while it shows the
 * crop the cut was made at. When the controls are open, or the crop is changed,
 * the banner shows the image positioned again; the next default save cuts it
 * again.
 *
 * Pure, and shared by the client and the server (the server image carries
 * src/libs).
 */

export const BAKED_BGS_FIELD = "bakedBgs";
export const BAKED_BG_META_FIELD = "bakedBgMeta";

/**
 * Channels that bake on a default save and wait for the default before they
 * show a background (the pilot).
 */
export const BAKED_BG_CHANNELS: ReadonlySet<string> = new Set(["social", "email", "releases"]);

/** The pixel ratio a baked image is cut at. Exports render at 2x as well. */
export const BAKED_BG_PIXEL_RATIO = 2;

export interface BakedBgFlip {
  x: boolean;
  y: boolean;
}

export interface BakedBgTransform {
  x: number;
  y: number;
  scale: number;
  rotate: number;
  flip: BakedBgFlip;
}

export interface BakedBgMeta extends BakedBgTransform {
  /** The image the crop was applied to. */
  src: string;
  /** The background box, in template CSS pixels. */
  width: number;
  height: number;
  pixelRatio: number;
}

type Rec<T> = Record<string, T | undefined> | null | undefined;

export interface BakedBgScene {
  bakedBgs?: Rec<string>;
  bakedBgMeta?: Rec<BakedBgMeta>;
  customBgs?: Rec<string>;
  bgX?: Rec<number>;
  bgY?: Rec<number>;
  bgScale?: Rec<number>;
  bgRotate?: Rec<number>;
  bgFlip?: Rec<Partial<BakedBgFlip>>;
  [field: string]: unknown;
}

const isFiniteNumber = (value: unknown): value is number =>
  typeof value === "number" && Number.isFinite(value);

/** The scene's crop for one template, or null when the scene has none. */
export function sceneTransformFor(
  scene: BakedBgScene,
  templateId: string
): BakedBgTransform | null {
  const x = scene.bgX?.[templateId];
  const y = scene.bgY?.[templateId];
  const scale = scene.bgScale?.[templateId];
  if (!isFiniteNumber(x) || !isFiniteNumber(y) || !isFiniteNumber(scale)) return null;
  const rotate = scene.bgRotate?.[templateId];
  const flip = scene.bgFlip?.[templateId];
  return {
    x,
    y,
    scale,
    rotate: isFiniteNumber(rotate) ? rotate : 0,
    flip: {x: Boolean(flip?.x), y: Boolean(flip?.y)},
  };
}

export function sameTransform(a: BakedBgTransform, b: BakedBgTransform): boolean {
  return (
    a.x === b.x &&
    a.y === b.y &&
    a.scale === b.scale &&
    a.rotate === b.rotate &&
    a.flip.x === b.flip.x &&
    a.flip.y === b.flip.y
  );
}

/** A crop as the editor or a record gives it: rotate and flip can be absent. */
export interface LooseBgTransform {
  x: number;
  y: number;
  scale: number;
  rotate?: number | null;
  flip?: Partial<BakedBgFlip> | null;
}

/** The crop with every field set: no rotate is 0, and no flip is no flip. */
export function fullTransform(t: LooseBgTransform): BakedBgTransform {
  return {
    x: t.x,
    y: t.y,
    scale: t.scale,
    rotate: isFiniteNumber(t.rotate) ? t.rotate : 0,
    flip: {x: Boolean(t.flip?.x), y: Boolean(t.flip?.y)},
  };
}

/** Natural size at the top left, with no rotate and no flip. */
export function isIdentityTransform(t: LooseBgTransform): boolean {
  return sameTransform(fullTransform(t), {
    x: 0,
    y: 0,
    scale: 1,
    rotate: 0,
    flip: {x: false, y: false},
  });
}

/**
 * Does the scene's baked image for `templateId` still show what the scene's
 * crop shows? True only when:
 *   - the image and its meta are both present;
 *   - the scene's crop for the template is the crop in the meta;
 *   - a preset image (`customBgs[templateId]`), when the scene has one, is the
 *     source in the meta.
 */
export function isBakedBgCurrent(scene: BakedBgScene, templateId: string): boolean {
  const url = scene.bakedBgs?.[templateId];
  const meta = scene.bakedBgMeta?.[templateId];
  if (!url || !meta || typeof meta.src !== "string") return false;

  const transform = sceneTransformFor(scene, templateId);
  if (!transform) return false;
  if (!sameTransform(transform, fullTransform(meta))) return false;

  const preset = scene.customBgs?.[templateId];
  if (preset && preset !== meta.src) return false;
  return true;
}

/**
 * The scene with only its current baked images (see isBakedBgCurrent). A scene
 * with none left has no `bakedBgs` / `bakedBgMeta` fields. Other fields are
 * not changed. Returns the same object when there is nothing to drop.
 */
export function withCurrentBakedBgs<T extends BakedBgScene | null | undefined>(scene: T): T {
  if (!scene || !scene.bakedBgs) return scene;
  const ids = Object.keys(scene.bakedBgs);
  const current = ids.filter((id) => isBakedBgCurrent(scene, id));
  if (current.length === ids.length && scene.bakedBgMeta) return scene;

  const {bakedBgs, bakedBgMeta, ...rest} = scene;
  if (current.length === 0) return rest as T;
  const keptBgs: Record<string, string> = {};
  const keptMeta: Record<string, BakedBgMeta> = {};
  for (const id of current) {
    keptBgs[id] = bakedBgs![id]!;
    keptMeta[id] = bakedBgMeta![id]!;
  }
  return {...rest, bakedBgs: keptBgs, bakedBgMeta: keptMeta} as T;
}

/**
 * What the page keeps for one baked banner: the image, the crop scale it was
 * cut at, and the image it was cut from. The editor applied blur to the image
 * before the crop scale, so the blur a banner shows is `blur * scale`; a baked
 * banner applies that.
 */
export interface BakedBgEntry {
  url: string;
  scale: number;
  /** The image the cut was made from (BakedBgMeta.src). */
  src?: string;
  /**
   * The crop the cut was made at. The banner shows the cut only while it
   * shows this crop (showsBakedCrop). Scenes saved before 2026-10-16 have
   * entries without it; loading such a scene adds it (withBakedCrops).
   */
  transform?: BakedBgTransform;
}

/**
 * Does a banner that shows the crop `shown` show its cut? True when the crop
 * is the one the cut was made at, or when the entry does not know its crop.
 */
export function showsBakedCrop(entry: BakedBgEntry, shown: LooseBgTransform): boolean {
  return !entry.transform || sameTransform(fullTransform(shown), entry.transform);
}

/**
 * The baked entries of a saved scene, each with the crop it was cut at. An
 * entry saved without one gets the scene's crop for its banner: a baked banner
 * had no controls when such a scene was saved, so the scene kept the crop of
 * the cut. Returns the same object when there is nothing to add.
 */
export function withBakedCrops(
  bakedBgs: Record<string, BakedBgEntry> | null | undefined,
  scene: BakedBgScene
): Record<string, BakedBgEntry> {
  if (!bakedBgs) return {};
  let out: Record<string, BakedBgEntry> | null = null;
  for (const [id, entry] of Object.entries(bakedBgs)) {
    if (!entry || typeof entry !== "object" || entry.transform) continue;
    const transform = sceneTransformFor(scene, id);
    if (!transform) continue;
    out ??= {...bakedBgs};
    out[id] = {...entry, transform};
  }
  return out ?? bakedBgs;
}

/**
 * The baked images of a scene, for the background the scene was resolved for.
 * Empty when there are none.
 */
export function bakedBgsOf(scene: BakedBgScene | null | undefined): Record<string, BakedBgEntry> {
  const current = withCurrentBakedBgs(scene);
  const out: Record<string, BakedBgEntry> = {};
  for (const [id, url] of Object.entries(current?.bakedBgs ?? {})) {
    const meta = current?.bakedBgMeta?.[id];
    if (url && meta) {
      out[id] = {url, scale: meta.scale, src: meta.src, transform: fullTransform(meta)};
    }
  }
  return out;
}

export interface BakedBgState {
  /** templateId → baked image, for the background in `option`. */
  bakedBgs: Record<string, BakedBgEntry>;
  /** The bgOption the baked images were loaded for. */
  option: string | null;
}

export const EMPTY_BAKED_BG_STATE: BakedBgState = Object.freeze({
  bakedBgs: Object.freeze({}) as Record<string, BakedBgEntry>,
  option: null,
}) as BakedBgState;

/**
 * The baked image a template shows now, or null. A baked image shows only for
 * the numbered gallery background it was loaded for: a shared upload
 * ("custom"), another background, or the template's own upload replaces it.
 *
 * `shownSrc` is the image the template would show positioned. Rows are keyed
 * by the background's number, so a number can come to name another image (a
 * list publish, a re-key). A cut of another image is not shown then: the
 * banner falls back to the crop. While `shownSrc` is not known yet (the
 * gallery is loading), the cut shows.
 */
export function bakedBgForTemplate({
  templateId,
  bgOption,
  state,
  hasOwnImage,
  shownSrc,
}: {
  templateId: string;
  bgOption: string | number | null | undefined;
  state: BakedBgState | null | undefined;
  hasOwnImage: boolean;
  shownSrc?: string | null;
}): BakedBgEntry | null {
  if (!state?.option || hasOwnImage) return null;
  if (bgOption === null || bgOption === undefined) return null;
  if (String(bgOption) !== state.option) return null;
  const n = Number(bgOption);
  if (!Number.isInteger(n) || n <= 0) return null;
  const entry = state.bakedBgs[templateId];
  if (!entry) return null;
  if (shownSrc && entry.src && shownSrc !== entry.src) return null;
  return entry;
}

/**
 * The baked images an applied default scene gives the page.
 *
 * `resolvedFor` is the background the cascade was merged for, and `displayed`
 * the background the page shows. They differ when the cascade ran for one
 * background and the page shows another (a URL background over a client-side
 * cascade). The scene's baked images then belong to another background, and
 * the page gets none.
 */
export function bakedBgStateForDefault({
  scene,
  resolvedFor,
  displayed,
}: {
  scene: BakedBgScene | null | undefined;
  resolvedFor: string | number | null | undefined;
  displayed: string | number | null | undefined;
}): BakedBgState {
  if (resolvedFor == null || displayed == null) return EMPTY_BAKED_BG_STATE;
  if (String(resolvedFor) !== String(displayed)) return EMPTY_BAKED_BG_STATE;
  const bakedBgs = bakedBgsOf(scene);
  if (Object.keys(bakedBgs).length === 0) return EMPTY_BAKED_BG_STATE;
  return {bakedBgs, option: String(displayed)};
}
